/**
 * Quick-filter buttons for the InvenTree parts table toolbar.
 *
 * Loaded globally into the InvenTree web UI by the plugin's response
 * middleware (see inventree_atlavox_shopify/middleware.py). Watches the DOM
 * and, whenever the parts list of a part-category page is visible, injects
 * Assembly / Salable / Component toggle buttons next to the table's action
 * buttons (print / barcode / add part).
 *
 * Filtering mechanism: InvenTree's table engine treats URL query parameters
 * as API filters which override the table's own stored filters. Toggling a
 * button rewrites the query string and fires a synthetic popstate event so
 * react-router (and therefore the table) picks up the change without a page
 * reload. Button state is derived from the URL, so it always reflects what
 * the table is actually showing.
 *
 * Fail-safe by design: if an InvenTree upgrade changes the toolbar DOM, the
 * anchor lookup simply fails and no buttons appear - nothing else breaks.
 */

const FILTERS = [
  { key: 'assembly', label: 'Assembly', title: 'Only parts that can be built from components' },
  { key: 'salable', label: 'Salable', title: 'Only parts that can be sold to customers' },
  { key: 'component', label: 'Component', title: 'Only parts that can be used in assemblies' }
] as const;

const CONTAINER_ID = 'atlavox-quick-filters';

/** Only the "Parts" panel of a part category page (index or specific). */
function onPartsList(): boolean {
  return /\/part\/category\/[^/]+\/parts\/?$/.test(window.location.pathname);
}

function isActive(key: string): boolean {
  return new URLSearchParams(window.location.search).get(key) === 'true';
}

function toggle(key: string): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get(key) === 'true') {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, 'true');
  }
  window.history.pushState(null, '', url.toString());
  // react-router only reacts to popstate, not to pushState made behind its back
  window.dispatchEvent(new PopStateEvent('popstate', { state: null }));
  render();
}

/** Mantine-theme-aware styling via CSS custom properties (light + dark). */
function styleButton(btn: HTMLButtonElement, active: boolean): void {
  btn.setAttribute('aria-pressed', String(active));
  const s = btn.style;
  s.display = 'inline-flex';
  s.alignItems = 'center';
  s.height = '2.1rem';
  s.padding = '0 0.75rem';
  s.borderRadius = 'var(--mantine-radius-default, 4px)';
  s.fontFamily = 'var(--mantine-font-family, sans-serif)';
  s.fontSize = 'var(--mantine-font-size-sm, 0.875rem)';
  s.fontWeight = '600';
  s.cursor = 'pointer';
  s.userSelect = 'none';
  if (active) {
    s.background = 'var(--mantine-color-blue-filled, #228be6)';
    s.color = 'var(--mantine-color-white, #fff)';
    s.border = '1px solid transparent';
  } else {
    s.background = 'transparent';
    s.color = 'var(--mantine-color-text, inherit)';
    s.border = '1px solid var(--mantine-color-default-border, #666)';
  }
}

function buildContainer(): HTMLElement {
  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  container.style.display = 'inline-flex';
  container.style.gap = '5px';
  container.style.marginLeft = '4px';

  for (const f of FILTERS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.filter = f.key;
    btn.title = f.title;
    btn.textContent = f.label;
    btn.addEventListener('click', () => toggle(f.key));
    styleButton(btn, isActive(f.key));
    container.appendChild(btn);
  }
  return container;
}

/** Sync button active-states with the current URL (cheap, run often). */
function render(): void {
  const container = document.getElementById(CONTAINER_ID);
  if (!container) return;
  container.querySelectorAll<HTMLButtonElement>('button[data-filter]').forEach((btn) => {
    styleButton(btn, isActive(btn.dataset.filter ?? ''));
  });
}

function ensureInjected(): void {
  const existing = document.getElementById(CONTAINER_ID);

  if (!onPartsList()) {
    existing?.remove();
    return;
  }

  if (existing) {
    render();
    return;
  }

  // Anchor on the table's refresh icon (stable aria-label), then walk to the
  // toolbar's left-hand action group: [actions...] <Space/> [search, refresh...]
  const refresh = document.querySelector('[aria-label="table-refresh"]');
  const outerGroup = refresh?.closest('.mantine-Group-root')?.parentElement;
  const actionGroup = outerGroup?.querySelector('.mantine-Group-root');
  if (!actionGroup) return;

  actionGroup.appendChild(buildContainer());
}

let scheduled = false;
const observer = new MutationObserver(() => {
  // Collapse mutation bursts into one check per animation frame
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    ensureInjected();
  });
});

observer.observe(document.body, { childList: true, subtree: true });
window.addEventListener('popstate', () => render());
ensureInjected();

export {};
